// One job, as rows and as the engine resolves it. The numbers on screen are always the
// engine's: resolved live from the rows while the job is open, never typed in.

import { useMemo, useRef } from 'react';
import { buildJob } from '@zerogap/db';
import { currentViews, resolveJob, type JobIn, type LibPricing, type Library, type Resolution, type ResolveOptions, type ResolvedItem, type Scalar } from '@zerogap/engine';
import { insertRow, useRows, type Row } from './api';
import { useLibrary } from './library';

export interface JobData {
  loading: boolean;
  job: Row | null;
  opportunity: Row | null;
  jobStandards: Row[];
  items: Row[];
  subLines: Row[];
  decisionLines: Row[];
  /** Job-wide costs: tax, shipping and handling that belong to no item. */
  costLines: Row[];
  /** The job's rooms, in order, and the standards a room holds for itself. */
  rooms: Row[];
  roomStandards: Row[];
  /** The job route (Spencer, 2026-10-08): the job type's steps and lines, and what this job typed against them or added. */
  typeSteps: Row[];
  typeBomLines: Row[];
  routeLines: Row[];
  bomLines: Row[];
  builds: Row[];
  addOns: Row[];
  /** The sheets in force: a sheet that a later one replaced is left out. */
  views: Row[];
  /** Every sheet, including those a later revision replaced. */
  allViews: Row[];
  marks: Row[];
}

export function useJobData(jobId: string | undefined): JobData {
  const on = { enabled: !!jobId };
  const eq = { job_id: jobId };
  const job = useRows('job', { eq: { id: jobId }, ...on });
  const jobRow = job.rows[0] ?? null;
  const opp = useRows('opportunity', { eq: { id: jobRow?.opportunity_id }, enabled: !!jobRow });
  const jobStandards = useRows('job_standard', { eq, ...on });
  const items = useRows('job_item', { eq, order: ['sort', 'created_at'], ...on });
  const subLines = useRows('sub_line', { eq, order: ['sort', 'created_at'], ...on });
  const decisionLines = useRows('decision_line', { eq, order: ['created_at'], ...on });
  const costLines = useRows('job_cost_line', { eq, order: ['sort', 'created_at'], ...on });
  const rooms = useRows('room', { eq, order: ['sort', 'created_at'], ...on });
  const roomStandards = useRows('room_standard', { in: { room_id: rooms.rows.map((r) => r.id) } });
  const typeSteps = useRows('job_type_route_step', { eq: { job_type_id: jobRow?.job_type_id }, order: ['sort', 'created_at'], enabled: !!jobRow?.job_type_id });
  const typeBomLines = useRows('job_type_bom_line', { eq: { job_type_id: jobRow?.job_type_id }, order: ['sort', 'created_at'], enabled: !!jobRow?.job_type_id });
  const routeLines = useRows('job_route_line', { eq, order: ['sort', 'created_at'], ...on });
  const bomLines = useRows('job_bom_line', { eq, order: ['sort', 'created_at'], ...on });
  const builds = useRows('build', { eq, order: ['sort', 'created_at'], ...on });
  const addOns = useRows('add_on', { eq, order: ['sort', 'created_at'], ...on });
  const views = useRows('view', { eq, order: ['sort', 'created_at'], ...on });
  const marks = useRows('mark', { in: { view_id: views.rows.map((v) => v.id) } });
  return {
    loading: job.loading || jobStandards.loading || items.loading || subLines.loading || decisionLines.loading || costLines.loading || rooms.loading || roomStandards.loading || routeLines.loading || bomLines.loading || builds.loading || addOns.loading || views.loading,
    job: jobRow,
    opportunity: opp.rows[0] ?? null,
    jobStandards: jobStandards.rows,
    items: items.rows,
    subLines: subLines.rows,
    decisionLines: decisionLines.rows,
    costLines: costLines.rows,
    rooms: rooms.rows,
    roomStandards: roomStandards.rows,
    typeSteps: typeSteps.rows,
    typeBomLines: typeBomLines.rows,
    routeLines: routeLines.rows,
    bomLines: bomLines.rows,
    // a retired build or add-on counts nowhere (2026-10-29): nothing is deleted, Restore (through the connector) brings it back
    builds: builds.rows.filter((b) => !b.retired_at),
    addOns: addOns.rows.filter((a) => !a.retired_at),
    views: currentViews(views.rows as any[]),
    allViews: views.rows,
    marks: marks.rows,
  };
}

export interface Resolved {
  loading: boolean;
  lib: Library | null;
  jobIn: JobIn | null;
  res: Resolution | null;
  error: string | null;
  /** Resolve the same job a different way: a Build's overrides, an Add-on's swap. */
  resolveWith: (opts: ResolveOptions) => Resolution | null;
}

/**
 * The job_item columns that are not pricing inputs: the Checked mark, an approval, an answer, a verification stamp, the
 * row's own timestamps. A Checked mark is not a pricing input, so toggling it never re-prices the job (the Quick check
 * pack, Spencer 2026-10-23, change log 1).
 */
const NOT_PRICING = new Set(['checked_at', 'checked_by', 'check_version_id', 'check_cleared_at', 'approved', 'answered', 'unverified_since', 'unverified_by', 'created_by', 'created_at', 'updated_at']);

/**
 * The item rows as the engine prices them: the same array reference for as long as no pricing input changes, so a
 * memo keyed on it does not re-run when a check, an approval or an answer lands on a row.
 */
export function usePricingRows(rows: Row[]): Row[] {
  const key = useMemo(
    () =>
      JSON.stringify(
        rows.map((r) => {
          const o: Row = {};
          for (const [k, v] of Object.entries(r)) if (!NOT_PRICING.has(k)) o[k] = v;
          return o;
        }),
      ),
    [rows],
  );
  const kept = useRef<{ key: string; rows: Row[] }>({ key, rows });
  if (kept.current.key !== key) kept.current = { key, rows };
  return kept.current.rows;
}

export function useResolved(data: JobData): Resolved {
  const { lib, rows, loading: libLoading } = useLibrary(data.job?.standards_version_id ?? null);
  const items = usePricingRows(data.items);
  return useMemo(() => {
    const loading = libLoading || data.loading;
    if (loading || !lib || !data.job) return { loading, lib, jobIn: null, res: null, error: null, resolveWith: () => null };
    try {
      const jobIn = buildJob(
        { job: data.job, job_standard: data.jobStandards, job_item: items, sub_line: data.subLines, decision_line: data.decisionLines, job_cost_line: data.costLines, room: data.rooms, room_standard: data.roomStandards, job_type_route_step: data.typeSteps, job_type_bom_line: data.typeBomLines, job_route_line: data.routeLines, job_bom_line: data.bomLines },
        rows.standard_def,
      );
      const res = resolveJob(lib, jobIn);
      const resolveWith = (opts: ResolveOptions) => {
        try {
          return resolveJob(lib, jobIn, opts);
        } catch {
          return null;
        }
      };
      return { loading: false, lib, jobIn, res, error: null, resolveWith };
    } catch (e) {
      return { loading: false, lib, jobIn: null, res: null, error: e instanceof Error ? e.message : String(e), resolveWith: () => null };
    }
  }, [lib, libLoading, rows.standard_def, data.loading, data.job, data.jobStandards, items, data.subLines, data.decisionLines, data.costLines, data.rooms, data.roomStandards, data.typeSteps, data.typeBomLines, data.routeLines, data.bomLines]);
}

/**
 * A child the engine created (a box's door) has no row until someone changes something on
 * it. This gives it one, so an override or a note has somewhere to live.
 */
export async function ensureStored(item: ResolvedItem, jobId: string, all: ResolvedItem[]): Promise<string> {
  if (item.stored) return item.id;
  const parent = all.find((i) => i.id === item.parent_item_id);
  if (!parent) throw new Error('This item has no parent to attach to.');
  const parentId = await ensureStored(parent, jobId, all);
  const row = await insertRow('job_item', {
    job_id: jobId,
    number: item.number,
    name: item.name,
    catalog_item_id: item.catalog_item_id,
    parent_item_id: parentId,
    // which of the parent's children this is: the link that creates it, and its place in that link
    catalog_child_id: item.child_link_id,
    child_index: item.child_index,
    sort: item.sort,
  });
  return row.id as string;
}

/** What an Add-on changes, in the engine's terms. */
export function addOnOptions(addOn: Row): ResolveOptions {
  const swap = (addOn.recipe_swap ?? {}) as { recipe_id?: string; overrides?: Record<string, any>; catalog_item_id?: string; inputs?: Record<string, any> };
  const targets = (addOn.target_items ?? []) as string[];
  const itemPatches: NonNullable<ResolveOptions['itemPatches']> = {};
  for (const id of targets) itemPatches[id] = { recipe_id: swap.recipe_id, overrides: swap.overrides, catalog_item_id: swap.catalog_item_id, inputs: swap.inputs };
  // the items the add-on holds are priced only with it on (2026-10-17)
  return { itemPatches, addOns: [addOn.id as string] };
}

/**
 * The job priced a chosen way: a Build's overrides and any Add-ons' swaps together. The Pricing picker in the
 * job's header chooses them, and every tab then reads that pricing (Spencer, 2026-10-14: the laundry room three ways).
 */
export function pricingOptions(build: Row | null, addOns: Row[]): ResolveOptions {
  const out: ResolveOptions = {};
  // a Build's standard overrides apply whether or not it is the base (2026-10-17: the base is a choice, not "as entered")
  const o = (build?.global_overrides ?? {}) as Record<string, Scalar>;
  if (build && Object.keys(o).length > 0) out.standardOverrides = o;
  // its own hours on the job route, while the job's "Different job routes per build" switch is on (the switch off clears them; 2026-10-22)
  const route = (build?.route_overrides ?? {}) as Record<string, Scalar>;
  if (build && Object.keys(route).length > 0) out.routeQty = route;
  // and its own percentages, over the job's (2026-10-17)
  const pct = (build?.pricing_overrides ?? {}) as Partial<LibPricing>;
  if (build && Object.keys(pct).length > 0) out.pricing = pct;
  // and the sub lines' cells under it: a cost typed for the build, or a line left out of its price (2026-10-24)
  if (build) out.buildId = build.id as string;
  const patches: NonNullable<ResolveOptions['itemPatches']> = {};
  for (const a of addOns) Object.assign(patches, addOnOptions(a).itemPatches);
  if (Object.keys(patches).length > 0) out.itemPatches = patches;
  if (addOns.length > 0) out.addOns = addOns.map((a) => a.id as string);
  return out;
}

/** True when these options change nothing: the job as entered (a Build always prices on its own: its sub line cells may differ). */
export function plainPricing(opts: ResolveOptions): boolean {
  return !opts.standardOverrides && !opts.pricing && !opts.itemPatches && !opts.addOns && !opts.routeQty && !opts.buildId;
}

/** The job's base Build: the one the proposal leads with and every tab prices by default (null when it has none). */
export function baseBuild(builds: Row[]): Row | null {
  return builds.find((b) => b.is_base) ?? null;
}

/**
 * The job priced under a Build with some Add-ons taken: the resolution already in hand when nothing changes,
 * else resolved again. Every screen that prices a Build or an Add-on reads through this (2026-10-17).
 */
export function pricedUnder(r: Resolved, build: Row | null, addOns: Row[]): Resolution | null {
  const opts = pricingOptions(build, addOns);
  return plainPricing(opts) ? r.res : r.resolveWith(opts);
}

/** The four percentages a Build may set for itself (Spencer, 2026-10-17; the large-purchase markup retired 2026-10-22), in the order the Price tab shows them. */
export const BUILD_PCTS: { key: keyof LibPricing; label: string }[] = [
  { key: 'gross_margin_pct', label: 'Gross margin' },
  { key: 'materials_markup_pct', label: 'Materials markup' },
  { key: 'sub_markup_pct', label: 'Sub markup' },
  { key: 'labor_contingency_pct', label: 'Labor contingency' },
];
